/**
 * Phones are stored normalized in E.164 (e.g. "+972521234567"), but people expect to see
 * the local Israeli form they typed when the fund was opened ("0521234567").
 * Numbers from other countries keep their international prefix.
 */
export const toLocalPhone = (phone?: string | null): string => {
  if (!phone) return '';
  const cleaned = phone.trim().replace(/[\s-]/g, '');
  if (cleaned.startsWith('+972')) return '0' + cleaned.slice(4);
  if (cleaned.startsWith('972')) return '0' + cleaned.slice(3);
  return cleaned;
};

export const ISRAELI_MOBILE_PREFIXES = ['050', '051', '052', '053', '054', '055', '056', '057', '058', '059'] as const;
export const ISRAELI_LANDLINE_PREFIXES = ['02', '03', '04', '08', '09'] as const;

const SUBSCRIBER_DIGITS = 7;

export type ParsedIsraeliPhone = {
  prefix: string;
  number: string;
  /** True when digits after the prefix exceeded seven — UI should explain instead of silent drop. */
  subscriberOverflow?: boolean;
};

export const parseIsraeliPhone = (phone: string, prefixes: readonly string[]): ParsedIsraeliPhone => {
  const digits = toLocalPhone(phone).replace(/\D/g, '');
  const prefix = [...prefixes].sort((a, b) => b.length - a.length).find((p) => digits.startsWith(p)) ?? '';
  if (prefix) {
    const subscriber = digits.slice(prefix.length);
    const number = subscriber.slice(0, SUBSCRIBER_DIGITS);
    const subscriberOverflow = subscriber.length > SUBSCRIBER_DIGITS;
    return { prefix, number, subscriberOverflow };
  }
  return { prefix: '', number: digits.slice(0, 10) };
};

/** Full Israeli mobile typed into the subscriber box (05 + 8 digits). */
export const isFullIsraeliMobileDigits = (digits: string): boolean => /^05\d{8}$/.test(digits);
